import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DataSource } from '@angular/cdk/collections';
import { merge, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { CorrespondenceService } from '../../../domain/services/correspondence/correspondence.service';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { CorrespondenceTemplate } from '../../../domain/models/correspondence.model';

class TemplateListDataSource extends DataSource<CorrespondenceTemplate> {
  paginator: MatPaginator | undefined;
  sort: MatSort | undefined;

  constructor(public data: CorrespondenceTemplate[]) {
    super();
  }

  connect(): Observable<CorrespondenceTemplate[]> {
    if (this.paginator && this.sort) {
      return merge(of(this.data), this.paginator.page, this.sort.sortChange).pipe(
        map(() => this.getPagedData(this.getSortedData([...this.data])))
      );
    }
    return of(this.data);
  }

  disconnect(): void {}

  private getPagedData(data: CorrespondenceTemplate[]): CorrespondenceTemplate[] {
    if (this.paginator) {
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      return data.slice(start, start + this.paginator.pageSize);
    }
    return data;
  }

  private getSortedData(data: CorrespondenceTemplate[]): CorrespondenceTemplate[] {
    if (!this.sort?.active || this.sort.direction === '') return data;
    return data.sort((a, b) => {
      const isAsc = this.sort!.direction === 'asc';
      switch (this.sort!.active) {
        case 'name': return compare(a.name, b.name, isAsc);
        case 'subject': return compare(a.subject, b.subject, isAsc);
        case 'lastModified': return compare(new Date(a.lastModified).getTime(), new Date(b.lastModified).getTime(), isAsc);
        case 'status': return compare(Number(a.active), Number(b.active), isAsc);
        default: return 0;
      }
    });
  }
}

function compare(a: string | number, b: string | number, isAsc: boolean): number {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

@Component({
  selector: 'app-template-list',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './template-list.component.html',
  styleUrl: './template-list.component.css'
})
export class TemplateListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<CorrespondenceTemplate>;

  dataSource = new TemplateListDataSource([]);
  allTemplates: CorrespondenceTemplate[] = [];

  displayedColumns = ['name', 'subject', 'lastModified', 'status', 'actions'];

  filterStatus: 'ACTIVE' | 'INACTIVE' | '' = '';
  searchQuery = '';

  readonly statuses = ['ACTIVE', 'INACTIVE'];

  constructor(
    private readonly correspondenceService: CorrespondenceService,
    private readonly breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'mail', url: '/app/correspondence/emails', label: 'Correspondence' },
      { icon: '', url: '/app/correspondence/templates', label: 'Templates' }
    ]);
  }

  ngAfterViewInit(): void {
    this.correspondenceService.getTemplates().subscribe(templates => {
      this.allTemplates = templates;
      this.rebuildDataSource(this.applyFilters(templates));
    });
  }

  rebuildDataSource(data: CorrespondenceTemplate[]): void {
    this.dataSource = new TemplateListDataSource(data);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    if (this.table) {
      this.table.dataSource = this.dataSource;
    }
  }

  applyFilters(data: CorrespondenceTemplate[]): CorrespondenceTemplate[] {
    let filtered = [...data];
    if (this.filterStatus) {
      filtered = filtered.filter(t => t.active === (this.filterStatus === 'ACTIVE'));
    }
    if (this.searchQuery.trim().length > 1) {
      const q = this.searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q)
      );
    }
    return filtered;
  }

  onFilter(): void {
    this.rebuildDataSource(this.applyFilters(this.allTemplates));
    if (this.paginator) this.paginator.pageIndex = 0;
  }

  onSearch(): void {
    this.onFilter();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onFilter();
  }

  getStatusClass(active: boolean): string {
    return active ? 'status-active' : 'status-inactive';
  }
}
